import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import type { AuditException } from '@/types/asset-audit';
import { UserPlus } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

interface AssignUserModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  exception: AuditException | null;
  onAssign: (data: AssignUserData) => void;
}

export interface AssignUserData {
  userId: string;
  userName: string;
  notes?: string;
}

// Mock users - in real app, fetch from API
const mockUsers = [
  { id: 'user-1', name: 'John Doe', email: 'john.doe@company.com' },
  { id: 'user-2', name: 'Jane Smith', email: 'jane.smith@company.com' },
  { id: 'user-3', name: 'Bob Johnson', email: 'bob.johnson@company.com' },
  { id: 'user-4', name: 'Alice Williams', email: 'alice.williams@company.com' },
  { id: 'user-5', name: 'Charlie Brown', email: 'charlie.brown@company.com' },
];

export function AssignUserModal({
  open,
  onOpenChange,
  exception,
  onAssign,
}: Readonly<AssignUserModalProps>) {
  const [selectedUserId, setSelectedUserId] = useState('');
  const [notes, setNotes] = useState('');

  React.useEffect(() => {
    if (exception) {
      setSelectedUserId(exception.assignedTo ?? '');
      setNotes('');
    }
  }, [exception]);

  const handleAssign = () => {
    if (!selectedUserId) return;
    
    const selectedUser = mockUsers.find(u => u.id === selectedUserId);
    if (!selectedUser) return;

    onAssign({
      userId: selectedUserId,
      userName: selectedUser.name,
      notes: notes.trim() || undefined,
    });
    onOpenChange(false);
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (!exception) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Assign Exception
          </DialogTitle>
          <DialogDescription>
            Assign this exception to a team member for review and resolution.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Exception Info */}
          <div className="rounded-lg bg-gray-50 p-3 text-sm">
            <div className="font-medium mb-1">Exception</div>
            <div className="text-gray-600">
              <div>{exception.exceptionType}</div>
              <div className="text-xs mt-1">
                {exception.assetCode} - {exception.assetName}
              </div>
            </div>
          </div>

          {/* Current Assignment */}
          {exception.assignedTo && (
            <div className="text-sm text-gray-600">
              <span className="font-medium">Currently assigned to:</span>{' '}
              {exception.assignedToName}
            </div>
          )}

          {/* User Selection */}
          <div className="space-y-2">
            <Label htmlFor="user">Assign To</Label>
            <Select value={selectedUserId} onValueChange={setSelectedUserId}>
              <SelectTrigger id="user">
                <SelectValue placeholder="Select a user" />
              </SelectTrigger>
              <SelectContent>
                {mockUsers.map((user) => (
                  <SelectItem key={user.id} value={user.id}>
                    <div className="flex items-center gap-2">
                      <Avatar className="h-6 w-6">
                        <AvatarImage src={`https://api.dicebear.com/7.x/initials/svg?seed=${user.name}`} />
                        <AvatarFallback className="text-xs">
                          {getInitials(user.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="font-medium">{user.name}</div>
                        <div className="text-xs text-gray-500">{user.email}</div>
                      </div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes (Optional)</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNotes(e.target.value)}
              placeholder="Add any notes or instructions for the assignee..."
              rows={3}
              className="resize-none"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleAssign} disabled={!selectedUserId}>
            <UserPlus className="h-4 w-4 mr-2" />
            Assign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
